/* eslint-disable no-console */
import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';

/**
 * Fetch all unique ratio pack names from purchase order templates
 * @returns {Promise<Array<string>>}
 */
export const fetchPurchaseOrderRatioPackNames = async (data) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}`, {
      params: { ...data },
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const addPurchaseOrderTemplate = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const updatePurchaseOrderTemplate = async (data) => {
  try {
    const { _id, ...updateData } = data;
    return await apiClient.put(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}/${_id}`, updateData);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Delete one or more Purchase Order Templates by IDs
 * @param {{templateIds: string[]}} data
 * @returns {Promise<any>}
 */
export const deletePurchaseOrderTemplate = async (data) => {
  try {
    const ids = Array.isArray(data?.templateIds) ? data.templateIds.join(',') : data?.templateIds;
    return await apiClient.delete(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}?templateIds=${ids}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Update isActive for one or more Purchase Order Templates
 * @param {{templateId: string, isActive: boolean}} data
 * @returns {Promise<any>}
 */
export const updatePurchaseOrderTemplateActive = async (data) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}-active`, {
      templateId: data?.templateId,
      isActive: data?.isActive,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Duplicate a Purchase Order Template by ID with a new name
 * @param {{templateId: string, newName: string, createdBy?: string, modifiedBy?: string}} data
 * @returns {Promise<any>}
 */
export const duplicatePurchaseOrderTemplate = async (data) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}/duplicate`, data);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getPurchaseOrderTemplateDetails = async (id) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}/${id}`);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch simple list of Purchase Order Templates for dropdown population
 * @param {Object} params - Query parameters
 * @param {boolean} [params.isActive=true] - Filter by active status
 * @returns {Promise<Array<{_id: string, templateName: string}>>}
 */
export const fetchPurchaseOrderTemplateList = async (params = {}) => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}/list`, {
      params: { isActive: params.isActive !== undefined ? params.isActive : true },
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Rearrange sections in a Purchase Order Template
 * @param {string} templateId - The ID of the template
 * @param {Array} sections - Array of sections with updated sequences
 * @returns {Promise<any>}
 */
export const rearrangePurchaseOrderTemplateSections = async (templateId, sections) => {
  try {
    return await apiClient.put(`${apiEndpoint}${API.PURCHASE_ORDER_TEMPLATE.GENERAL}/${templateId}/sections`, {
      sections,
    });
  } catch (error) {
    console.error(error);
    throw error;
  }
};

/**
 * Fetch old purchase order template libraries with optimized performance
 * @param {Object} params - Query parameters including pagination and search
 * @param {number} [params.pageSize=20] - Number of items per page (max 100)
 * @param {number} [params.pageIndex=1] - Page number (1-based)
 * @param {string} [params.searchText] - Search text for filtering
 * @param {string} [params.poId] - Specific PO ID for lookup
 * @param {boolean} [params.isActive] - Filter by active status
 * @returns {Promise<any>}
 */
export const fetchOldPurchaseOrderTemplateLibraries = async (params = {}) => {
  try {
    const sanitizedParams = {
      pageSize: Math.min(parseInt(params.pageSize) || 20, 100),
      pageIndex: Math.max(parseInt(params.pageIndex) || 1, 1),
      ...(params.searchText?.trim() && { searchText: params.searchText.trim() }),
      ...(params.isActive !== undefined && { isActive: Boolean(params.isActive) }),
    };

    return await apiClient.get(`${apiEndpoint}/old-purchase-order-template`, {
      params: sanitizedParams,
      timeout: 30000,
    });
  } catch (error) {
    console.error('Error in fetchOldPurchaseOrderTemplateLibraries:', error);
    throw error;
  }
};

/**
 * Delete a single Old Purchase Order Template by ID
 * @param {string} templateId - Single template ID
 * @returns {Promise<any>}
 */
export const deleteOldPurchaseOrderTemplateSingle = async (templateId) => {
  try {
    return await apiClient.delete(`${apiEndpoint}/po-template-libraries/${templateId}`);
  } catch (error) {
    console.error('Error in deleteOldPurchaseOrderTemplateSingle:', error);
    throw error;
  }
};

/**
 * Delete multiple Old Purchase Order Templates by IDs (bulk delete)
 * @param {string[]} templateIds - Array of template IDs
 * @returns {Promise<any>}
 */
export const deleteOldPurchaseOrderTemplateBulk = async (templateIds) => {
  try {
    const payload = {
      multipleDelete: true,
      poTemplateLibIds: JSON.stringify(templateIds),
    };
    return await apiClient.delete(`${apiEndpoint}/po-template-libraries/1`, { data: payload });
  } catch (error) {
    console.error('Error in deleteOldPurchaseOrderTemplateBulk:', error);
    throw error;
  }
};

/**
 * Delete one or more Old Purchase Order Templates by IDs
 * Automatically chooses between single and bulk delete based on input
 * @param {{templateIds: string[]}} data
 * @returns {Promise<any>}
 */
export const deleteOldPurchaseOrderTemplate = async (data) => {
  try {
    const ids = Array.isArray(data?.templateIds) ? data.templateIds : [data?.templateIds];

    if (ids.length === 1) {
      return await deleteOldPurchaseOrderTemplateSingle(ids[0]);
    }
    return await deleteOldPurchaseOrderTemplateBulk(ids);
  } catch (error) {
    console.error('Error in deleteOldPurchaseOrderTemplate:', error);
    throw error;
  }
};

/**
 * Fetch preview data for a Purchase Order Template by ID
 * @param {string} id - The ID of the template to preview
 * @returns {Promise<any>}
 */
export const fetchPreviewData = async (id) => {
  try {
    return await apiClient.get(`${apiEndpoint}/po-template-libraries/${id}`);
  } catch (error) {
    console.error('Error in fetchPreviewData:', error);
    throw error;
  }
};
